import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { nameof } from 'utils/typeHelpers';

import { LocationSearchResult } from 'components/_controls/AoiSearch/models';

import {
  ConnectivityGroupByRequest,
  ConnectivityProvinceGroupByDisclaimerStatus,
  DefaultConnectivityGroupBy,
  DefaultModSort,
  DefaultMopSort,
} from './connectivity.models';

export type ConnectivityState = {
  originLocation: LocationSearchResult;
  destinationLocation: LocationSearchResult;
  loadingLocationsInfo: boolean;
  loadingDiseaseRisks: boolean;
  loadingTravelVolumes: boolean;
  loadingModAggRow: boolean;
  travelVolumes: dto.TravelVolumeListModel;
  activeMopSort: dto.TravelVolumeSortType;
  activeModSort: dto.DiseaseMovementSortType;
  activeOriginGroupBy: dto.ConnectivityGroupByType;
  activeDestinationGroupBy: dto.ConnectivityGroupByType;
  groupByProvinceDisclaimerStatus: ConnectivityProvinceGroupByDisclaimerStatus;
  includeDomestic: boolean;
  includeGroundTravel: boolean;
  isGroundTravelAllowed: boolean;
  allModDiseases: Record<number, string>;
  filteredDiseaseIds: number[];
  modAggVolumes: dto.DiseaseRiskVolumeModel[];
  diseaseRisks: dto.DiseaseMovementModel[];
  diseaseRisksAvail: number;
};

export const InitialConnectivityState: ConnectivityState = {
  originLocation: null,
  destinationLocation: null,
  loadingLocationsInfo: false,
  loadingDiseaseRisks: false,
  loadingTravelVolumes: false,
  loadingModAggRow: false,
  travelVolumes: null,
  activeMopSort: DefaultMopSort,
  activeModSort: DefaultModSort,
  activeOriginGroupBy: DefaultConnectivityGroupBy,
  activeDestinationGroupBy: DefaultConnectivityGroupBy,
  groupByProvinceDisclaimerStatus: 'hidden',
  includeDomestic: false,
  includeGroundTravel: false,
  isGroundTravelAllowed: false,
  allModDiseases: {},
  filteredDiseaseIds: [],
  modAggVolumes: null,
  diseaseRisks: [],
  diseaseRisksAvail: null,
};

const connectivitySlice = createSlice({
  name: nameof<InsightsState>('connectivity'),
  initialState: InitialConnectivityState,
  reducers: {
    startLoadingLocations: state => {
      state.loadingLocationsInfo = true;
      state.loadingTravelVolumes = true;
      state.loadingDiseaseRisks = true;
      state.travelVolumes = null;
    },
    startLoadingDiseaseRisks: state => {
      state.loadingDiseaseRisks = true;
    },
    startLoadingTravelVolumes: state => {
      state.loadingTravelVolumes = true;
    },
    startLoadingModAggRow: state => {
      state.loadingModAggRow = true;
    },
    startRefreshingDiseaseRisks: state => {
      state.loadingDiseaseRisks = true;
      state.diseaseRisks = [];
    },
    setModAggRowInfo: (state, action: PayloadAction<dto.DiseaseRiskVolumeModel[]>) => {
      state.modAggVolumes = action.payload;
      state.loadingModAggRow = false;
    },
    setModAggRowDiseases: (state, action: PayloadAction<dto.DiseaseRiskVolumeModel[]>) => {
      action.payload.forEach(({ diseaseId, diseaseName }) => {
        state.allModDiseases[diseaseId] = diseaseName;
      });
    },
    setFilteredDiseases: (state, action: PayloadAction<number[]>) => {
      state.filteredDiseaseIds = action.payload;
    },
    setDiseaseRisks: (state, action: PayloadAction<dto.DiseaseMovementListModel>) => {
      state.diseaseRisks = action.payload.diseaseMovements;
      state.diseaseRisksAvail = action.payload.total;
      state.loadingDiseaseRisks = false;
    },
    appendPaginatedDiseaseRisks: (state, action: PayloadAction<dto.DiseaseMovementModel[]>) => {
      state.diseaseRisks = [...state.diseaseRisks, ...action.payload];
      state.loadingDiseaseRisks = false;
    },
    resetTravelVolumeInfo: state => {
      state.activeMopSort = DefaultMopSort;
      state.travelVolumes = null;
    },
    resetDiseaseRiskInfo: state => {
      state.activeModSort = DefaultModSort;
      state.diseaseRisks = [];
      state.diseaseRisksAvail = null;
      state.allModDiseases = {};
      state.filteredDiseaseIds = [];
    },
    startRefreshingTravelVolumes: state => {
      state.loadingTravelVolumes = true;
      state.travelVolumes = null;
    },
    clearODLocations: state => {
      state.originLocation = null;
      state.destinationLocation = null;
      state.travelVolumes = null;
    },
    setGroupByDisclaimerStatus: (
      state,
      action: PayloadAction<ConnectivityProvinceGroupByDisclaimerStatus>
    ) => {
      if (state.groupByProvinceDisclaimerStatus !== 'user-hidden')
        state.groupByProvinceDisclaimerStatus = action.payload;
    },
    setODLocations: (
      state,
      action: PayloadAction<{ origin: LocationSearchResult; destination: LocationSearchResult }>
    ) => {
      const { origin, destination } = action.payload;

      state.originLocation = origin;
      state.destinationLocation = destination;

      /**
       * If both origin & destination exist, can only be domestic if there is the same countryId
       * and can't be domestic if the ids are false. This is a way to enforce the disable state
       * on the domestic toggle make sense for what is happening in the query
       */
      if (origin != null && destination != null && origin.countryId === destination.countryId)
        state.includeDomestic = true;
      else state.includeDomestic = false;

      state.loadingLocationsInfo = false;
    },
    setODInfo: (state, action: PayloadAction<dto.TravelVolumeListModel>) => {
      state.travelVolumes = action.payload;
      state.loadingTravelVolumes = false;
    },
    appendODInfo: (state, action: PayloadAction<dto.TravelVolumeListModel>) => {
      state.travelVolumes.travelVolumes = [
        ...state.travelVolumes.travelVolumes,
        ...action.payload.travelVolumes,
      ];

      state.loadingTravelVolumes = false;
    },
    setActiveMopSort: (state, action: PayloadAction<dto.TravelVolumeSortType>) => {
      state.activeMopSort = action.payload;
    },
    setActiveModSort: (state, action: PayloadAction<dto.DiseaseMovementSortType>) => {
      state.activeModSort = action.payload;
    },
    setActiveGroupBy: (state, action: PayloadAction<ConnectivityGroupByRequest>) => {
      state.activeOriginGroupBy = action.payload.originGroupBy;
      state.activeDestinationGroupBy = action.payload.destinationGroupBy;
    },
    errorLoadingData: state => {
      state.travelVolumes = null;
      state.loadingLocationsInfo = false;
      state.loadingTravelVolumes = false;
    },
    errorLoadingPaginationData: state => {
      state.loadingLocationsInfo = false;
      state.loadingTravelVolumes = false;
    },
    toggleIncludeDomestic: state => {
      state.includeDomestic = !state.includeDomestic;
    },
    toggleIncludeGroundTravel: state => {
      state.includeGroundTravel = !state.includeGroundTravel;
    },
    setIncludeDomestic: (state, action: PayloadAction<boolean>) => {
      state.includeDomestic = action.payload;
    },
    setIncludeGroundTravel: (state, action: PayloadAction<boolean>) => {
      state.includeGroundTravel = action.payload;
    },
    setIsGroundTravelAllowed: (state, action: PayloadAction<boolean>) => {
      state.isGroundTravelAllowed = action.payload;
    },
    errorLoadingModAggRow: state => {
      state.modAggVolumes = null;
      state.loadingModAggRow = false;
    },
    errorLoadingDiseaseRisks: state => {
      state.loadingDiseaseRisks = false;
    },
  },
});

const { actions, reducer } = connectivitySlice;

export const {
  setODLocations,
  startLoadingDiseaseRisks,
  startLoadingTravelVolumes,
  startLoadingLocations,
  startLoadingModAggRow,
  startRefreshingDiseaseRisks,
  setModAggRowInfo,
  setModAggRowDiseases,
  setFilteredDiseases,
  setDiseaseRisks,
  appendPaginatedDiseaseRisks,
  resetTravelVolumeInfo,
  resetDiseaseRiskInfo,
  setODInfo,
  appendODInfo,
  setActiveMopSort,
  setActiveModSort,
  startRefreshingTravelVolumes,
  setActiveGroupBy,
  clearODLocations,
  errorLoadingData,
  errorLoadingPaginationData,
  setGroupByDisclaimerStatus,
  toggleIncludeDomestic,
  toggleIncludeGroundTravel,
  setIncludeDomestic,
  setIncludeGroundTravel,
  setIsGroundTravelAllowed,
  errorLoadingModAggRow,
  errorLoadingDiseaseRisks,
} = actions;

export default reducer;
